import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import { useAppContext, Apps, features } from '../contexts/AppContext';
import { useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import DeleteIcon from '@mui/icons-material/Delete';
import { Feature, Project } from '../types';
import { Server } from '../services/Server';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { handleError } from '../utils/Error';
import Dialog from '../components/common/Dialog';

const Projects = () => {
  const queryClient = useQueryClient();
  const { setApp, setNewRole, isDarkMode } = useAppContext();
  const { getProjectsAPI, deleteProjectAPI, getCleaningPackagesAPI } = Server();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);

  useEffect(() => {
    setApp(Apps.SoilingMitigation);
    document.title = 'Projects | ' + Apps.SoilingMitigation;
  }, []);

  const {
    data: projects = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['soiling-assessment-projects'],
    queryFn: () => getProjectsAPI(),
  });

  const { data: cleaningPackages = {}, isLoading: isLoadingCleaningPackages } = useQuery({
    queryKey: ['cleaning_packages'],
    queryFn: () => getCleaningPackagesAPI(),
  });

  if (isError) {
    handleError(error);
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProjectAPI(id),
    onSuccess: () => {
      toast.success('Success!');
      queryClient.invalidateQueries({ queryKey: ['soiling-assessment-projects'] });
    },
    onError: (error) => {
      handleError(error);
    },
  });

  const getHyperlink = (name: string, id: string) => {
    return (
      <>
        <Link
          href={'/project/' + id}
          sx={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            navigate('/project/' + id);
          }}
        >
          {name}
        </Link>
      </>
    );
  };

  const getChip = (value: number) => {
    if (value < 25) {
      return { label: 'Low Risk', color: 'chip-green', value: value };
    }
    if (value < 50) {
      return { label: 'Moderate Risk', color: 'chip-yellow', value: value };
    }
    if (value < 75) {
      return { label: 'High Risk', color: 'chip-orange', value: value };
    }
    if (value < 100) {
      return { label: 'Very High Risk', color: 'chip-red', value: value };
    }
    return { label: 'N/A', color: 'chip-transparent', value: 'N/A' };
  };

  const getSolutionDisplayName = (technicalName: string) => {
    if (!technicalName || !cleaningPackages[technicalName]) {
      return technicalName || 'N/A';
    }
    return cleaningPackages[technicalName].name || technicalName;
  };

  const handleDeleteClick = (projectId: string) => {
    setProjectToDelete(projectId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (projectToDelete) {
      deleteMutation.mutate(projectToDelete);
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const columns = useMemo<MRT_ColumnDef<Project>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 180,
        Cell: ({ cell, row }) => {
          const name = cell.getValue<string>();
          return (
            <Tooltip
              title={name}
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#fff',
                    color: '#000',
                    boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
                    fontSize: '0.875rem',
                    border: '1px solid #e0e0e0',
                  },
                },
              }}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getHyperlink(name, row.original.id)}
              </Box>
            </Tooltip>
          );
        },
        muiTableBodyCellProps: {
          align: 'left',
        },
      },
      {
        accessorKey: 'location.name',
        header: 'Location',
        size: 150,
        muiTableBodyCellProps: {
          align: 'left',
        },
      },
      {
        accessorFn: (row: any) => row.results?.global_soiling_index,
        id: 'global_soiling_index',
        header: 'Soiling Level',
        size: 150,
        muiTableBodyCellProps: {
          align: 'left',
        },
        Cell: ({ cell }) => {
          let chip = getChip(cell.getValue<number>());
          return (
            <Tooltip
              title={'Soiling Level : ' + (typeof chip.value === 'number' ? (100 - chip.value).toFixed(2) : 'N/A')}
              sx={{ cursor: 'pointer' }}
            >
              <Chip
                label={chip.label}
                className={chip.color}
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        accessorFn: (row: any) => row.results?.solution,
        id: 'solution',
        header: 'Cleaning Solution',
        muiTableBodyCellProps: {
          align: 'left',
        },
        size: 200,
        Cell: ({ cell }) => {
          const technicalName = cell.getValue<string>();
          const displayName = getSolutionDisplayName(technicalName);
          return (
            <Tooltip title={technicalName !== displayName ? `Technical name: ${technicalName}` : ''}>
              <span>{displayName}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorFn: (row: any) => row.results?.cleaning_frequency,
        id: 'cleaning_frequency',
        header: 'Cleaning Frequency',
        size: 200,
        muiTableBodyCellProps: {
          align: 'left',
        },
      },
      {
        accessorFn: (row: any) => row.results?.needed_devices,
        id: 'needed_devices',
        header: 'Number of Devices',
        size: 200,
        muiTableBodyCellProps: {
          align: 'left',
        },
      },
      {
        header: 'Action',
        Cell: ({ row }) => {
          return (
            <Box sx={{ display: 'inline' }}>
              <Tooltip title="Delete">
                <IconButton color="error" onClick={() => handleDeleteClick(row.original.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
        size: 100,
        muiTableBodyCellProps: {
          align: 'left',
        },
      },
    ],
    [cleaningPackages],
  );

  const table = useMaterialReactTable({
    columns,
    data: projects,
    initialState: {
      density: 'compact',
    },
    state: {
      isLoading: isLoading || isLoadingCleaningPackages,
    },
    enableEditing: false,
    enableDensityToggle: false,
    layoutMode: 'grid',
    muiTableContainerProps: {
      sx: { backgroundColor: 'rgba(255, 255, 255, 0.5)', paddingLeft: 2, paddingRight: 2 },
    },
  });

  return (
    <>
      <Box
        sx={{
          borderRadius: '10px',
          margin: 0,
          height: '100vh',
          display: 'flex',
          backgroundImage: isDarkMode ? 'URL(/smt-bg-dark.png)' : 'URL(/smt-bg.png)',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          overflow: 'scroll',
        }}
      >
        <Box
          sx={{
            borderRadius: '10px',
            p: 0,
            margin: 'auto',
            mt: 10,
            maxWidth: '95%',
          }}
        >
          <Grid container spacing={2}>
            {features.map((item: Feature) => (
              <Grid
                key={item.name}
                sx={{
                  borderRadius: '10px',
                  p: 0,
                  margin: 'auto',
                  maxWidth: 1200,
                  flexGrow: 1,
                }}
                item
                sm={3}
              >
                <Tooltip title={item.description} arrow>
                  <CardActionArea
                    onClick={() => {
                      setNewRole(item.role);
                      navigate(item.path);
                    }}
                  >
                    <Card sx={{ display: 'flex', height: 100 }}>
                      <CardMedia component="img" sx={{ width: 151 }} image={'/' + item.img} title={item.name}></CardMedia>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flex: '1 0 auto' }}>
                          <Typography gutterBottom variant="h5" component="div">
                            Create a new {item.name}
                          </Typography>
                        </CardContent>
                      </Box>
                    </Card>
                  </CardActionArea>
                </Tooltip>
              </Grid>
            ))}
            <Grid item sm={12}>
              <MaterialReactTable table={table} />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} title={{ text: 'Delete Project' }} size="sm">
        <Typography sx={{ mb: 3 }}>Are you sure you want to delete this project? This action cannot be undone.</Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Stack>
      </Dialog>
    </>
  );
};

export default Projects;
